import {useMemo} from 'react';
import {Body2, Headline3} from '@ui/atoms/typography';
import {Button} from '@ui/atoms/button';
import {ItemLoader} from '@ui/organisms/item-loader';
import {Wrapper, GridItem, HistoryList} from '../my_checkins-style';
import {CompanyCheckinsController} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.controller';
import {useQuery} from 'react-query';
import dayjs from 'dayjs';
import {SubmissionHistoryItem} from './submission-history-item';

type EmojiMap = Record<
  string,
  {
    emoji: any;
    label: string;
  }
>;

export interface SubmissionHistoryEntry {
  id: string;
  period: string;
  submittedOn: string;
  summary: string;
  highlights: string[];
  moodKey: string;
  note: string;
  pulse?: {
    mood: string;
    feedback: string | null;
  };
  responses?: any;
}

interface CheckinHistoryProps {
  emojiMap: EmojiMap;
  onDismiss: () => void;
  expandedSubmissions: Set<string>;
  submissionDetails: Record<string, any>;
  onToggleExpand: (submissionId: string) => void;
  checkinsController: CompanyCheckinsController;
  currentCycleId: string | null;
  userId: string;
}

export const CheckinHistory = ({
  emojiMap,
  onDismiss,
  expandedSubmissions,
  submissionDetails,
  onToggleExpand,
  checkinsController,
  currentCycleId,
  userId,
}: CheckinHistoryProps) => {
  // Get list of submitted checkins for current cycle only
  const {data: checkinsList, isLoading: isLoadingCheckinsList} = useQuery(
    ['user-checkins-list', userId, currentCycleId],
    async () => {
      if (!currentCycleId) {
        return [];
      }

      try {
        const response = await checkinsController.getListOfCheckins({
          user: userId,
          isSubmitted: true,
          paginate: false,
          // Add cycle filter to only get current cycle submissions
        });

        if (response && response.data && Array.isArray(response.data)) {
          // Filter by current cycle ID on the frontend
          return response.data.filter((submission: any) => {
            return submission.cycle && submission.cycle === currentCycleId;
          });
        }
        return [];
      } catch (error) {
        console.error('Failed to fetch checkins list:', error);
        return [];
      }
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!userId && !!currentCycleId,
    },
  );

  // Convert checkins list to submission history format
  const submissionHistory = useMemo<SubmissionHistoryEntry[]>(() => {
    if (!checkinsList || !Array.isArray(checkinsList)) {
      return [];
    }

    return checkinsList.map((submission: any) => ({
      ...submission,
      id: submission.id,
      period:
        submission.period ||
        `Week ${submission.weekOfYear || ''} · ${submission.year || ''}`,
      submittedOn: submission.submittedAt
        ? dayjs(submission.submittedAt).format('MMM D, YYYY')
        : '',
      summary: submission.summary || '',
      highlights: submission.highlights || [],
      moodKey: submission.mood || 'neutral',
      note: submission.note || '',
      pulse: submission.pulse || null,
    }));
  }, [checkinsList]);

  return (
    <GridItem dividerSide="left">
      <Wrapper>
        <div className="mr-6">
          <div className="flex justify-between items-center border-b border-b-[#EDEDF2] pb-3 mb-6">
            <Headline3>Submission History</Headline3>

            <Button
              kind="secondary"
              width="sm"
              type="button"
              style={{padding: '8px 16px'}}
              onClick={onDismiss}>
              Dismiss
            </Button>
          </div>

          <HistoryList>
            {isLoadingCheckinsList ? (
              <div className="flex justify-center py-8">
                <ItemLoader />
              </div>
            ) : submissionHistory.length === 0 ? (
              <Body2 kind="textBody">No submission history available.</Body2>
            ) : (
              submissionHistory.map((entry) => (
                <SubmissionHistoryItem
                  key={entry.id}
                  entry={entry}
                  emojiMap={emojiMap}
                  isExpanded={expandedSubmissions.has(entry.id)}
                  submissionDetails={submissionDetails}
                  checkinsController={checkinsController}
                  onToggleExpand={onToggleExpand}
                />
              ))
            )}
          </HistoryList>
        </div>
      </Wrapper>
    </GridItem>
  );
};
